import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import {
    AppointmentStatusEnum,
    CallStatusEnum,
    DULUX__EVENT__APPOINTMENT__APPOINTMENT_STATUS__COLUMN_ID,
    DULUX__EVENT__CALLS__CALL_STATUS__COLUMN_ID,
    DULUX__EVENT__CALLS__SCHEDULE_DATE__COLUMN_ID,
    DULUX__EVENT__ONBOARDING__ONBOARDING_STATUS__COLUMN_ID,
    DuluxEventTypes,
    OnboardingStatusEnum,
} from "~/domainModels/workspaceSpecific/dulux";
import {
    DULUX__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    DULUX__EVENT_CONTAINER__NAME__COLUMN_ID,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/dulux/EventContainers";
import {AnankeClockIcon, AnankePersonIcon} from "~/domainModels/workspaceSpecific/icons";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uniqueUuid} from "@vesta/unique/unique";

type DuluxCallEditRow = {
    label: string;
    isEditable: boolean;
    isRequired?: boolean;
    columnType: "OttopilotEventContainer" | "OttopilotEvent";
    columnId: Uuid;
};

export function buildDuluxCallEditPanelTemplate(params: {eventTemplate: OttopilotEventTemplate}): OttopilotEventTemplate {
    const {eventTemplate} = params;
    if (eventTemplate.id !== (DuluxEventTypes.Calls as Uuid)) {
        return eventTemplate;
    }

    const toSectionColumns = (rows: Array<DuluxCallEditRow>) =>
        rows.map((row) => {
            const base = {
                id: `${String(row.columnId)}::${row.label}` as Uuid,
                isEditable: row.isEditable,
                isRequired: row.isRequired === true,
                label: row.label,
                columnId: row.columnId,
                isAdditionalColumn: true,
            };
            if (row.columnType === "OttopilotEvent") {
                return {
                    ...base,
                    columnType: "OttopilotEvent" as const,
                    eventTemplateId: eventTemplate.id,
                };
            }
            return {
                ...base,
                columnType: "OttopilotEventContainer" as const,
            };
        });

    return {
        ...eventTemplate,
        configuration: {
            ...eventTemplate.configuration,
            eventPage: {
                ...eventTemplate.configuration.eventPage,
                editPanel: {
                    columnGrouping: [
                        {
                            id: uniqueUuid`dulux-call-edit-lead-details`,
                            displayName: "Lead details",
                            iconId: AnankePersonIcon,
                            iconColor: "var(--ds-icon-accent-blue)",
                            iconBackgroundColor: "var(--ds-background-accent-blue-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Lead name",
                                    isEditable: false,
                                    columnType: "OttopilotEventContainer",
                                    columnId: DULUX__EVENT_CONTAINER__NAME__COLUMN_ID,
                                },
                                {
                                    label: "Contact number",
                                    isEditable: false,
                                    columnType: "OttopilotEventContainer",
                                    columnId: DULUX__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
                                },
                                {
                                    label: "Call status",
                                    isEditable: false,
                                    columnType: "OttopilotEvent",
                                    columnId: DULUX__EVENT__CALLS__CALL_STATUS__COLUMN_ID,
                                },
                            ]),
                        },
                        {
                            id: uniqueUuid`dulux-call-edit-schedule`,
                            displayName: "Schedule",
                            iconId: AnankeClockIcon,
                            iconColor: "var(--ds-icon-accent-yellow)",
                            iconBackgroundColor: "var(--ds-background-accent-yellow-subtlest)",
                            initialIsOpen: true,
                            columns: toSectionColumns([
                                {
                                    label: "Schedule time",
                                    isEditable: true,
                                    isRequired: true,
                                    columnType: "OttopilotEvent",
                                    columnId: DULUX__EVENT__CALLS__SCHEDULE_DATE__COLUMN_ID,
                                },
                            ]),
                        },
                    ],
                },
            },
        },
    };
}

export function isDuluxScheduledCallEvent(event: {additionalColumnValues: Record<Uuid, unknown>}): boolean {
    return event.additionalColumnValues[DULUX__EVENT__CALLS__CALL_STATUS__COLUMN_ID] === CallStatusEnum.SCHEDULED;
}

export function isDuluxScheduledAppointmentEvent(event: {additionalColumnValues: Record<Uuid, unknown>}): boolean {
    return (
        event.additionalColumnValues[DULUX__EVENT__APPOINTMENT__APPOINTMENT_STATUS__COLUMN_ID] ===
        AppointmentStatusEnum.SCHEDULED
    );
}

export function isDuluxNotOnboardedOnboardingEvent(event: {additionalColumnValues: Record<Uuid, unknown>}): boolean {
    return (
        event.additionalColumnValues[DULUX__EVENT__ONBOARDING__ONBOARDING_STATUS__COLUMN_ID] ===
        OnboardingStatusEnum.NOT_ONBOARDED
    );
}
